import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, Send, Car, Calendar, DollarSign, Phone, Mail, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyMessages = ({ lang, user }) => {
  const { t } = useTranslation(lang);
  const [conversations, setConversations] = useState({ price_quotes: [], test_drives: [], contact_messages: [] });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    if (user) {
      fetchConversations();
    }
  }, [user]);

  const fetchConversations = async () => {
    try {
      // Use customer portal API with email (phone is optional)
      const response = await axios.get(`${API}/customer-portal/conversations`, {
        params: { 
          email: user.email,
          phone: user.phone || ''
        }
      });
      
      setConversations({
        price_quotes: response.data.price_quotes || [],
        test_drives: response.data.test_drives || [],
        contact_messages: response.data.contact_messages || []
      });
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error(t('messages.loadError'));
    }
  };

  const handleReply = async (requestType, requestId) => {
    if (!replyMessage.trim()) {
      toast.error(t('messages.pleaseWriteMessage'));
      return;
    }

    setSendingReply(true);
    try {
      const token = localStorage.getItem('token');
      
      let endpoint = '';
      if (requestType === 'price-quote') {
        endpoint = `${API}/price-quotes/${requestId}/customer-reply`;
      } else if (requestType === 'test-drive') {
        endpoint = `${API}/test-drives/${requestId}/customer-reply`;
      } else if (requestType === 'contact') {
        endpoint = `${API}/contact/${requestId}/customer-reply`;
      }

      await axios.post(
        endpoint,
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Mesajınız gönderildi!');
      setReplyMessage('');
      
      // Refresh conversations
      await fetchConversations();
      
      // Update selected conversation
      const allConvs = [
        ...conversations.price_quotes.map(q => ({ ...q, type: 'price-quote' })),
        ...conversations.test_drives.map(td => ({ ...td, type: 'test-drive' })),
        ...conversations.contact_messages.map(m => ({ ...m, type: 'contact' }))
      ];
      const updated = allConvs.find(c => c.id === requestId);
      if (updated) {
        setSelectedConversation(updated);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Mesaj gönderilemedi');
    } finally {
      setSendingReply(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Redirect if not logged in
  if (!user) {
    return (
      <>
        <SEOHead 
          title="My Messages | AAA of Miami"
          description="View and manage your messages at AAA of Miami"
        />
        <div className="pt-20 min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-secondary)' }}>
        <div className="max-w-md w-full mx-4">
          <div className="rounded-lg shadow-xl p-8 text-center" style={{ background: 'var(--bg-primary)' }}>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'var(--accent)' }}>
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
              Mesajlarınız İçin Giriş Yapın
            </h1>
            <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
              Mesajlarınızı görüntülemek için lütfen giriş yapın
            </p>
            <Button onClick={() => window.location.href = '/login'} className="w-full">
              Giriş Yap
            </Button>
          </div>
        </div>
      </div>
      </>
    );
  }

  // Messages dashboard
  const allConversations = [
    ...conversations.price_quotes.map(q => ({ ...q, type: 'price-quote', icon: DollarSign, typeLabel: 'Fiyat Teklifi' })),
    ...conversations.test_drives.map(td => ({ ...td, type: 'test-drive', icon: Calendar, typeLabel: 'Test Sürüşü' })),
    ...conversations.contact_messages.map(m => ({ ...m, type: 'contact', icon: MessageSquare, typeLabel: t('contact.contactMessage') }))
  ].sort((a, b) => {
    const aLatest = a.conversation?.[a.conversation.length - 1]?.timestamp || a.created_at;
    const bLatest = b.conversation?.[b.conversation.length - 1]?.timestamp || b.created_at;
    return new Date(bLatest) - new Date(aLatest);
  });

  return (
    <>
      <SEOHead 
        title="My Messages | AAA of Miami"
        description="View and manage your messages at AAA of Miami"
      />
      <div className="pt-20 min-h-screen" style={{ background: 'var(--bg-secondary)' }}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
            {t('messages.myMessages')}
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            {user.email}
          </p>
        </div>

        {allConversations.length === 0 ? (
          <div className="text-center py-16 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
            <MessageSquare className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
            <p className="text-xl" style={{ color: 'var(--text-secondary)' }}>
              {t('messages.noMessages')}
            </p>
          </div>
        ) : (
          <div className="grid gap-6">
            {allConversations.map((conv) => {
              const Icon = conv.icon;
              const lastMessage = conv.conversation?.[conv.conversation.length - 1];
              const hasUnreadAdmin = lastMessage && lastMessage.from === 'admin';

              return (
                <div
                  key={conv.id}
                  className="rounded-lg shadow-md p-6 cursor-pointer hover:shadow-lg transition-shadow"
                  style={{ background: 'var(--bg-primary)', borderLeft: hasUnreadAdmin ? '4px solid var(--accent)' : 'none' }}
                  onClick={() => setSelectedConversation(conv)}
                >
                  <div className="flex gap-4">
                    {/* Vehicle Image */}
                    {conv.vehicle && conv.vehicle.images?.[0] && (
                      <img
                        src={conv.vehicle.images[0]}
                        alt={`${conv.vehicle.brand} ${conv.vehicle.model}`}
                        className="w-32 h-24 object-cover rounded"
                      />
                    )}

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                        <span className="font-semibold" style={{ color: 'var(--accent)' }}>
                          {conv.typeLabel}
                        </span>
                        {hasUnreadAdmin && (
                          <span className="px-2 py-1 text-xs rounded" style={{ background: 'var(--accent)', color: 'white' }}>
                            Yeni Yanıt
                          </span>
                        )}
                      </div>

                      {conv.vehicle && (
                        <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
                          {conv.vehicle.year} {conv.vehicle.brand} {conv.vehicle.model}
                        </h3>
                      )}

                      <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>
                        {conv.message || conv.notes || 'Mesaj yok'}
                      </p>

                      {lastMessage && (
                        <div className="mt-3 p-3 rounded" style={{ background: 'var(--bg-secondary)' }}>
                          <p className="text-sm font-semibold mb-1" style={{ color: lastMessage.from === 'admin' ? 'var(--accent)' : 'var(--text-primary)' }}>
                            {lastMessage.from === 'admin' ? '🏢 AAA OF MIAMI' : '👤 Siz'}:
                          </p>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {lastMessage.message.substring(0, 100)}{lastMessage.message.length > 100 ? '...' : ''}
                          </p>
                          <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
                            {formatDate(lastMessage.timestamp)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Conversation Detail Dialog */}
        <Dialog open={!!selectedConversation} onOpenChange={() => setSelectedConversation(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            {selectedConversation && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Car className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                    {selectedConversation.vehicle && (
                      <span>
                        {selectedConversation.vehicle.year} {selectedConversation.vehicle.brand} {selectedConversation.vehicle.model}
                      </span>
                    )}
                  </DialogTitle>
                </DialogHeader>

                {/* Conversation Messages */}
                <div className="space-y-4 my-4">
                  {/* Initial Message */}
                  <div className="p-4 rounded" style={{ background: 'var(--bg-secondary)' }}>
                    <p className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                      👤 {t('messages.yourInitialMessage')}
                    </p>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      {selectedConversation.message || selectedConversation.notes}
                    </p>
                    <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
                      {formatDate(selectedConversation.created_at)}
                    </p>
                  </div>

                  {/* Conversation Thread */}
                  {selectedConversation.conversation?.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded ${msg.from === 'admin' ? 'ml-0 mr-8' : 'ml-8 mr-0'}`}
                      style={{ background: msg.from === 'admin' ? 'var(--bg-secondary)' : 'var(--bg-primary)', border: msg.from === 'customer' ? '1px solid var(--border)' : 'none' }}
                    >
                      <p className="font-semibold mb-2" style={{ color: msg.from === 'admin' ? 'var(--accent)' : 'var(--text-primary)' }}>
                        {msg.from === 'admin' ? '🏢 AAA OF MIAMI' : `👤 ${t('messages.you')}`}:
                      </p>
                      <p style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                        {msg.message}
                      </p>
                      <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
                        {formatDate(msg.timestamp)}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Reply Form */}
                <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
                  <label className="block mb-2 font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {t('messages.replyLabel')}
                  </label>
                  <Textarea
                    placeholder={t('messages.writeYourMessage')}
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={4}
                    className="mb-3"
                  />
                  <Button type="submit" className="w-full" disabled={sendingReply}
                    onClick={() => handleReply(selectedConversation.type, selectedConversation.id)}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingReply ? t('messages.sending') : t('messages.send')}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
    </>
  );
};

export default MyMessages;
